import { memo } from 'react'

import { Save, Warning } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography, styled } from '@mui/material'
import { AudioStream, VideoStream } from '@nativewaves/nw-cloud-sdk/content'
import { motion } from 'motion/react'
import { Controller, FormProvider } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { ConfirmButton } from '@shared/components/Confirm/ConfirmButton'
import { appearance } from '@shared/utils/support'
import { DurationField } from 'components/DurationField'
import { StreamEdit } from 'domains/Workspaces/Streams'
import { EditableSection } from 'domains/Workspaces/Streams/AvcTaskAssociations'
import { useStreamGeneralForm } from 'domains/Workspaces/Streams/General/useStreamGeneralForm'

const StyledForm = styled(motion.form)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  height: '100%',
  overflowY: 'auto',
}))

export type GeneralFormProps = {
  stream: VideoStream | AudioStream
  save: (edit: StreamEdit, options?: { onSuccess?: () => void; onError?: () => void }) => void
  isSaving: boolean
  onSaved: () => void
  onCancel: () => void
}

const GeneralFormRaw = ({ stream, save, isSaving, onSaved, onCancel }: GeneralFormProps) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])

  const { formMethods, handleSubmit, willScheduleDeletion } = useStreamGeneralForm({ stream, save, onSaved })
  const { control, formState, reset, watch } = formMethods

  const isAutodelete = watch('isAutodelete')

  return (
    <FormProvider {...formMethods}>
      <StyledForm onSubmit={handleSubmit} variants={appearance} initial="hidden" animate="visible">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button type="button" variant="text" disabled={isSaving} onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button type="button" variant="outlined" disabled={!formState.isDirty || isSaving} onClick={() => reset()}>
            {t('reset')}
          </Button>
          {/* A lifecycle change schedules content for deletion, so it gets a second click. */}
          {willScheduleDeletion ? (
            <ConfirmButton
              variant="contained"
              startIcon={<Save />}
              isLoading={isSaving}
              disabled={!formState.isDirty}
              onConfirm={handleSubmit}
              staticLabel={t('save')}
            />
          ) : (
            <LoadingButton type="submit" variant="contained" loading={isSaving} disabled={!formState.isDirty}>
              {t('save')}
            </LoadingButton>
          )}
        </Box>

        <Paper sx={{ px: 4, py: 3 }} elevation={0}>
          <Typography sx={{ mb: 2 }} variant="heading-md">
            {t('domain:Workspace.Streams.contentLifecycle')}
          </Typography>

          <Controller
            name="isAutodelete"
            control={control}
            render={({ field }) => (
              <ToggleButtonGroup sx={{ mb: 3 }} value={field.value} color="primary" size="small" exclusive>
                <ToggleButton value={false} onClick={() => field.onChange(false)} sx={{ textTransform: 'capitalize' }}>
                  {t('domain:Workspace.Streams.noExpiration')}
                </ToggleButton>
                <ToggleButton value onClick={() => field.onChange(true)} sx={{ textTransform: 'capitalize' }}>
                  {t('domain:Workspace.Streams.autoDeletion')}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          />

          <Controller
            name="contentLifecyclePolicy.deleteAfter"
            control={control}
            render={({ field }) => (
              <DurationField
                disabled={!isAutodelete}
                defaultValue={field.value}
                {...field}
                // An emptied input yields NaN, which would serialize to `deleteAfter: null`.
                onChange={(value) => field.onChange(Number.isFinite(value) ? value : 0)}
                label={t('deleteAfter')}
                // Both are hardcoded on the inner TextField; harmless in the standalone dialog,
                // but here autoFocus steals focus on every Edit and required blocks submit.
                textFieldProps={{ autoFocus: false, required: false }}
              />
            )}
          />

          {willScheduleDeletion && (
            <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ mt: 3, maxWidth: 480 }}>
              <Warning color="warning" />
              <Typography>
                <Trans ns="domain" i18nKey="Workspace.Streams.lifecycleWarning" />
              </Typography>
            </Stack>
          )}
        </Paper>

        <EditableSection repositoryId={stream.repositoryId} />
      </StyledForm>
    </FormProvider>
  )
}

export const GeneralForm = memo(GeneralFormRaw)
